import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toastr } from 'react-redux-toastr';
import { useParams } from 'react-router-dom';
import {
  set,
  get,
  getOr,
  size,
  uniqBy,
  groupBy,
  isEmpty,
  map,
  flow,
  filter
} from 'lodash/fp';
import { Button, ButtonGroup, Content, Icon, Radio, RadioGroup } from 'rsuite';
import styled from 'styled-components';
import {
  createROPicklist,
  deleteROPicklist,
  fetchROPicklistForView,
  updateROPicklistSorting
} from '../../../../redux/Reservation/sagas/routines';
import { getIsDeleting } from '../../../../redux/Reservation/selectors/ro-picklist';
import { getIsSaving } from '../../../../redux/Reservation/selectors/routes/ro-picklist/create';
import { resetROPicklistView } from '../../../../redux/Reservation/actions/routes/ro-picklist/view';
import { getROPicklistViewData } from '../../../../redux/Reservation/selectors/routes/ro-picklist/view';
import {
  viewAccess,
  modifyAccess,
  fullAccess
} from '../../../../helpers/permission';
import * as PERMISSIONS from '../../../../constants/permissions/reservation';
import pagePathToUrl from '../../../../helpers/pagePathToUrl';
import { parseInt } from '../../../../helpers/utils';
import {
  reservationsROPicklistEditPath,
  reservationsROPicklistListPath
} from '../../../Paths';
import history from '../../../history';
import PageHeader from '../../../../components/PageHeader';
import PageLayout from '../../../../components/reservations/PageLayout';
import AccessControl from '../../../../components/AccessControl';
import AccessDenied from '../../../../components/AccessDenied';
import ButtonToolbar from '../../../../components/reservations/ButtonToolbar';
import Breadcrumb from '../../../../components/Breadcrumb';
import SortableCategories from './SortableCategories';
import DeleteConfirmationModal from '../../../../components/DeleteConfirmationModal';
import clonePicklist from '../../../../helpers/clonePicklist';

const ViewSizes = {
  mobile: 360,
  tablet: 600,
  desktop: 1024
};

const PicklistPreview = styled.div`
  padding: 5px;
  border: 1px solid #efefef;
`;

const PicklistName = styled.h6`
  color: #fff;
  padding: 8px;
  text-align: center;
  text-transform: uppercase;
  background-color: #86cce7;
`;

const CommentLabel = styled.p`
  margin: 10px 0;
  text-align: center;
  text-transform: uppercase;
`;

export default () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const picklist = useSelector(getROPicklistViewData);
  const isDeleting = useSelector(getIsDeleting);
  const isCloning = useSelector(getIsSaving);
  const [categories, setCategories] = useState([]);
  const [itemsByCategory, setItemsByCategory] = useState({});
  const [viewSize, setViewSize] = useState(ViewSizes.mobile);

  useEffect(
    () => {
      dispatch(fetchROPicklistForView(id));
      return function reset() {
        dispatch(resetROPicklistView());
      };
    },
    [id]
  );

  useEffect(
    () => {
      if (picklist) {
        setCategories(picklist.categories);
        const filterdItems = flow(
          getOr([], 'items'),
          filter(
            ({ isVisible = 1, enabled = true }) =>
              (isVisible === true || parseInt(isVisible) === 1) && enabled
          )
        )(picklist);
        setItemsByCategory(groupBy('categoryId', filterdItems));
      }
    },
    [picklist]
  );

  useEffect(
    () => {
      const hasEmpty = size(itemsByCategory) !== size(categories);
      if (hasEmpty) {
        toastr.error(
          'Error',
          'Unable to view Pick List. All category must have at least one item.'
        );
        history.push(pagePathToUrl(reservationsROPicklistEditPath, { id }));
      }
    },
    [itemsByCategory, categories]
  );

  const handleOnSortCategories = value => {
    const { trackingMethod, ...body } = picklist;
    dispatch(
      updateROPicklistSorting({
        id,
        body: {
          ...body,
          trackingMethodId: trackingMethod.id,
          categories: map(
            each => ({ ...each, items: itemsByCategory[each.id] }),
            value
          )
        }
      })
    );
    setCategories(value);
  };

  const handleOnSortItems = ({ categoryId, items }) => {
    const { trackingMethod, ...body } = picklist;
    const updatedItemsByCategory = set(categoryId, items, itemsByCategory);
    dispatch(
      updateROPicklistSorting({
        id,
        body: {
          ...body,
          trackingMethodId: trackingMethod.id,
          categories: map(
            each => ({ ...each, items: updatedItemsByCategory[each.id] }),
            categories
          )
        }
      })
    );
    setItemsByCategory(updatedItemsByCategory);
  };

  const handleEdit = () => {
    history.push(pagePathToUrl(reservationsROPicklistEditPath, { id }));
  };

  const handleClone = () => {
    const { items } = picklist;
    const hasEmptyItems =
      size(uniqBy('categoryId', items)) !== size(categories);

    if (hasEmptyItems) {
      toastr.error(
        'Error',
        'Unable to Clone Pick List. Please fix form error first.'
      );
      history.push(pagePathToUrl(reservationsROPicklistEditPath, { id }));
    } else {
      dispatch(createROPicklist({ body: clonePicklist(picklist) }));
    }
  };

  const handleShowDeleteModal = () => {
    setShowDeleteModal(true);
  };

  const handleViewChange = value => {
    setViewSize(value);
  };

  const handleHideDeleteModal = () => {
    setShowDeleteModal(false);
  };

  const handleConfirmDeleteItem = () => {
    setShowDeleteModal(false);
    dispatch(deleteROPicklist(picklist));
    setTimeout(() => {
      history.push(reservationsROPicklistListPath);
    }, 200);
  };

  return (
    <PageLayout>
      <AccessControl
        allowOnlyFor={viewAccess(PERMISSIONS.RO_PICKLIST)}
        fallback={<AccessDenied />}
      >
        <Breadcrumb
          items={[
            {
              label: 'Pick Lists',
              path: reservationsROPicklistListPath
            },
            { label: getOr('...', 'name', picklist) }
          ]}
        />
        <PageHeader title="Pick List Preview" />
        <Content style={{ marginTop: 0 }}>
          <ButtonToolbar>
            <AccessControl allowOnlyFor={modifyAccess(PERMISSIONS.RO_PICKLIST)}>
              <ButtonGroup>
                <Button size="sm" appearance="primary" onClick={handleEdit}>
                  <Icon icon="pencil" /> Edit
                </Button>
              </ButtonGroup>
              <ButtonGroup>
                <Button
                  size="sm"
                  appearance="primary"
                  onClick={handleClone}
                  disabled={isCloning}
                >
                  <Icon icon="clone" /> Clone
                </Button>
              </ButtonGroup>
            </AccessControl>
            <AccessControl allowOnlyFor={fullAccess(PERMISSIONS.RO_PICKLIST)}>
              <ButtonGroup>
                <Button size="sm" onClick={handleShowDeleteModal} color="red">
                  <Icon icon="trash" /> Delete
                </Button>
              </ButtonGroup>
            </AccessControl>
          </ButtonToolbar>

          <RadioGroup
            inline
            name="radioList"
            value={viewSize}
            onChange={handleViewChange}
          >
            <Radio value={ViewSizes.mobile}>Mobile Phone View</Radio>
            <Radio value={ViewSizes.tablet}>Tablet View</Radio>
            <Radio value={ViewSizes.desktop}>Desktop View</Radio>
          </RadioGroup>

          <PicklistPreview style={{ width: viewSize }}>
            <PicklistName>{get('name', picklist)}</PicklistName>
            {!isEmpty(categories) && (
              <SortableCategories
                categories={categories}
                itemsByCategory={itemsByCategory}
                onSortEnd={handleOnSortCategories}
                itemsOnSortEnd={handleOnSortItems}
              />
            )}
            <hr />
            <CommentLabel>{get('notes', picklist)}</CommentLabel>
          </PicklistPreview>

          <DeleteConfirmationModal
            message="Are you sure you want to delete this Pick List?"
            show={showDeleteModal}
            onHide={handleHideDeleteModal}
            onConfirm={handleConfirmDeleteItem}
            isDeleting={isDeleting}
          />
        </Content>
      </AccessControl>
    </PageLayout>
  );
};
